import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Label, Input } from 'reactstrap';
import { FaArrowUp, FaArrowDown, FaTrash, FaPlus } from 'react-icons/fa';

// Admin editor for the customer's CSV export template. Every export in the app -
// the per-survey button, the whole-drillhole button and the bulk export - renders
// through this, so it decides what every CSV the customer downloads looks like.
//
// The template holds no flags that change what a column *means*: whether an
// azimuth is grid corrected, and whether an angle is dip or inclination, is
// decided by which column is in the list. That is deliberate. A downstream
// database can be fed a column called "Azimuth" that holds corrected values, and
// the field underneath still says plainly which reference it is.
//
// The column catalogue is fetched from api/surveys/export/options rather than
// mirrored here: the server drops any field it does not recognise, so a second
// copy on the client would silently offer columns that never appear.

// Explaining the two that can be confused for one another is the whole job of
// this screen, so the descriptions live next to the picker rather than in a doc.
const FIELD_NOTES = {
    Azimuth: 'True north, exactly as the gyro measured it. Never corrected.',
    AzimuthCorrected: 'True north plus the grid correction in force on each survey’s own date.',
    MagneticAzimuth: 'The magnetometer’s own reading — blank or zero on gyro-only tools, which is most of them. This is NOT the grid-corrected column.',
    Dip: 'Inclination minus 90°.',
    Inclination: '0° horizontal, 90° straight down.'
};

const LAYOUTS = [
    { value: 'Columns', label: 'No heading — identify each survey in columns' },
    { value: 'Heading', label: 'A heading line above each survey' },
    { value: 'Metadata', label: 'A full metadata block above each survey' }
];

export const ExportTemplateEditor = ({ template, onChange, angleDisplay, onAngleDisplayChange }) => {
    const [options, setOptions] = useState(null);
    const [error, setError] = useState('');
    const [addField, setAddField] = useState('');

    useEffect(() => {
        let cancelled = false;
        fetch('api/surveys/export/options')
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => { if (!cancelled) setOptions(data); })
            .catch(err => { if (!cancelled) setError(err.message); });
        return () => { cancelled = true; };
    }, []);

    if (error) {
        return <div className="alert alert-danger">Could not load export options: {error}</div>;
    }
    if (!options) {
        return <p className="text-muted"><em>Loading export template...</em></p>;
    }

    const catalogue = options.catalogue || [];
    // A customer who has never configured a template is on the default, and the
    // editor shows exactly what they are already getting rather than a blank form.
    const effective = template || options.defaultTemplate;
    if (!effective) {
        return <p className="text-muted"><em>Loading export template...</em></p>;
    }

    const columns = effective.columns || [];
    const update = (changes) => onChange({ ...effective, ...changes });
    const setColumns = (next) => update({ columns: next });
    const entryFor = (field) =>
        catalogue.find(c => c.field.toLowerCase() === (field || '').toLowerCase());

    const moveColumn = (index, delta) => {
        const next = [...columns];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setColumns(next);
    };

    const removeColumn = (index) => setColumns(columns.filter((_, i) => i !== index));

    const addColumn = () => {
        if (!addField) return;
        if (columns.some(c => c.field.toLowerCase() === addField.toLowerCase())) return;
        setColumns([...columns, { field: addField, header: '', decimals: null }]);
        setAddField('');
    };

    const patchColumn = (index, changes) =>
        setColumns(columns.map((c, i) => (i === index ? { ...c, ...changes } : c)));

    const unused = catalogue.filter(
        c => !columns.some(col => col.field.toLowerCase() === c.field.toLowerCase()));

    const usesCorrected = columns.some(c => (c.field || '').toLowerCase() === 'azimuthcorrected');
    const correction = options.gridCorrectionInForce;
    const hasCorrection = typeof correction === 'number';
    const correctionDate = options.gridCorrectionAppliesFrom
        ? new Date(options.gridCorrectionAppliesFrom).toLocaleDateString()
        : null;

    return (
        // A plain bordered panel rather than a .card: cards are themed solid red
        // app-wide by custom.css.
        <div className="border rounded bg-white p-3">
            <h5>Export Template</h5>
            <p className="text-muted">
                Controls every survey CSV downloaded from DrillTrak &mdash; the Export button on a
                survey, the whole-drillhole export, and the bulk export. Users choose the layout at
                export time; everything here is yours.
            </p>
            {!template && (
                <div className="alert alert-info" role="alert">
                    Showing the default template{options.industry ? ` for a ${String(options.industry).toLowerCase()} customer` : ''}.
                    Saving will store it as this organisation&apos;s own, unchanged.
                </div>
            )}

            <FormGroup>
                <Label for="angleDisplay">Angle shown on screen</Label>
                <Input type="select" id="angleDisplay"
                    value={angleDisplay || ''}
                    onChange={e => onAngleDisplayChange(e.target.value || null)}>
                    <option value="">
                        Follow the organisation&apos;s industry{options.prefersDip ? ' (Dip)' : ' (Inclination)'}
                    </option>
                    <option value="Dip">Dip</option>
                    <option value="Inclination">Inclination</option>
                </Input>
                <small className="text-muted">
                    Affects the survey tables on screen. What the exported file contains is decided
                    by whether you add the Dip or the Inclination column below.
                </small>
            </FormGroup>

            <FormGroup>
                <Label for="defaultLayout">Default layout for bulk export</Label>
                <Input type="select" id="defaultLayout"
                    value={effective.defaultLayout || 'Columns'}
                    onChange={e => update({ defaultLayout: e.target.value })}>
                    {LAYOUTS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Input>
                <small className="text-muted">A user can change this for a single export.</small>
            </FormGroup>

            <FormGroup>
                <Label for="shotOrder">Shot order</Label>
                <Input type="select" id="shotOrder"
                    value={effective.shotOrder === 'Natural' ? 'Natural' : 'Depth'}
                    onChange={e => update({ shotOrder: e.target.value })}>
                    <option value="Depth">Depth, shallowest first</option>
                    <option value="Natural">Natural order for the tool</option>
                </Input>
                <small className="text-muted">
                    A FastTrak run is a round trip, so ordering by depth interleaves the in-run and
                    out-run. Natural order keeps them apart; depth order is what exports have always
                    used.
                </small>
            </FormGroup>

            {/* The reference the customer's downstream database depends on, stated
                in numbers rather than described, so a wrong template is visible
                here rather than only in their data weeks later. */}
            {usesCorrected && (
                <div className={`alert ${hasCorrection ? 'alert-info' : 'alert-warning'}`} role="alert">
                    {hasCorrection ? (
                        <>
                            <strong>Grid correction in force: {correction}&deg;</strong>
                            {correctionDate ? ` (effective from ${correctionDate})` : ''}
                            <div className="small mt-1">
                                A shot reading 28.00&deg; from true north exports as{' '}
                                <strong>{((28 + correction) % 360).toFixed(2)}&deg;</strong> in the
                                Azimuth (Grid Corrected) column. Each survey uses the correction in
                                force on its own date, so older surveys may use an earlier value.
                            </div>
                        </>
                    ) : (
                        <>
                            <strong>No grid correction is configured.</strong>
                            <div className="small mt-1">
                                The Azimuth (Grid Corrected) column is in this template but there is
                                nothing to apply, so it will export the same values as true north.
                                Add a grid correction above, or use the plain Azimuth column.
                            </div>
                        </>
                    )}
                </div>
            )}

            <h6 className="mt-4">Columns</h6>
            {columns.length === 0 ? (
                <p className="text-muted"><em>No columns &mdash; the export would be empty.</em></p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm align-middle">
                        <thead>
                            <tr>
                                <th>Column</th>
                                <th>Heading in the file</th>
                                <th style={{ width: 110 }}>Decimals</th>
                                <th style={{ width: 130 }}>Order</th>
                            </tr>
                        </thead>
                        <tbody>
                            {columns.map((column, index) => {
                                const entry = entryFor(column.field);
                                const numeric = entry && entry.decimals !== null && entry.decimals !== undefined;
                                const note = FIELD_NOTES[entry ? entry.field : column.field];
                                return (
                                    <tr key={column.field}>
                                        <td>
                                            {entry ? entry.defaultHeader : column.field}
                                            {entry && entry.kind === 'Constant' &&
                                                <span className="badge bg-secondary ms-2"
                                                    title="One value for the whole survey">per survey</span>}
                                            {!entry &&
                                                <span className="badge bg-warning text-dark ms-2"
                                                    title="Not a known column - it will be left out of the file">unknown</span>}
                                            {note && <div className="small text-muted">{note}</div>}
                                        </td>
                                        <td>
                                            <Input bsSize="sm" type="text"
                                                value={column.header || ''}
                                                placeholder={entry ? entry.defaultHeader : column.field}
                                                onChange={e => patchColumn(index, { header: e.target.value })} />
                                        </td>
                                        <td>
                                            {numeric ? (
                                                <Input bsSize="sm" type="number" min="0" max="8"
                                                    value={column.decimals ?? ''}
                                                    placeholder={String(entry.decimals)}
                                                    onChange={e => patchColumn(index, {
                                                        decimals: e.target.value === '' ? null : parseInt(e.target.value, 10)
                                                    })} />
                                            ) : <span className="text-muted">&mdash;</span>}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <Button color="secondary" size="sm" className="me-1"
                                                disabled={index === 0} title="Move up"
                                                onClick={() => moveColumn(index, -1)}><FaArrowUp /></Button>
                                            <Button color="secondary" size="sm" className="me-1"
                                                disabled={index === columns.length - 1} title="Move down"
                                                onClick={() => moveColumn(index, 1)}><FaArrowDown /></Button>
                                            <Button color="danger" size="sm" title="Remove"
                                                onClick={() => removeColumn(index)}><FaTrash /></Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex align-items-center">
                <Input type="select" bsSize="sm" style={{ maxWidth: 380 }}
                    value={addField} onChange={e => setAddField(e.target.value)}>
                    <option value="">Add a column...</option>
                    {unused.map(entry => (
                        <option key={entry.field} value={entry.field}>
                            {entry.defaultHeader}{entry.kind === 'Constant' ? ' (per survey)' : ''}
                        </option>
                    ))}
                </Input>
                <Button color="secondary" size="sm" className="ms-2"
                    disabled={!addField} onClick={addColumn}><FaPlus /> Add</Button>
                {options.defaultTemplate && (
                    <Button color="secondary" size="sm" className="ms-auto"
                        title="Go back to the columns this customer gets with nothing configured"
                        onClick={() => onChange({ ...options.defaultTemplate })}>Reset to default</Button>
                )}
            </div>
            {addField && FIELD_NOTES[addField] && (
                <div className="small text-muted mt-2">{FIELD_NOTES[addField]}</div>
            )}
        </div>
    );
};
